import { z } from "zod";
import {
    AccountType,
    AccountSubtype,
    AccountClass,
} from "@common/constants/domains/accounting/accounts";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { AccountRole } from "@common/types/domains/accounting/account";
import {
    FORM_1099_BOX_FILTER_UNASSIGNED,
    Form1099Box,
} from "@common/constants/domains/accounting/form1099Box";

export const accountTypeSchema = z.nativeEnum(AccountType);

export const accountSubtypeSchema = z.nativeEnum(AccountSubtype);

export const accountClassSchema = z.nativeEnum(AccountClass);

export const accountRoleSchema = z.nativeEnum(AccountRole).nullish();

export const form1099BoxSchema = z.nativeEnum(Form1099Box).nullish();

export const form1099BoxFilterValueSchema = z.union([
    z.nativeEnum(Form1099Box),
    z.literal(FORM_1099_BOX_FILTER_UNASSIGNED),
]);

export const accountSchema = z.object({
    id: z.string(),
    code: z.number(),
    codeText: z.string().nullable(),
    name: z.string(),
    class: accountClassSchema,
    type: accountTypeSchema,
    subtype: accountSubtypeSchema,
    isDisabled: z.boolean(),
    isManuallyCreated: z.boolean(),
    financialAccountId: z.number().nullable(),
    kickOriginalCode: z.number().nullable(),
    parentGroupId: z.string().uuid().nullable(),
    sortOrder: z.number(),
    role: accountRoleSchema,
    form1099Box: form1099BoxSchema,
    relatedEntityId: z.number().nullish(),
});

export type AccountSchema = z.infer<typeof accountSchema>;

export const ACCOUNT_CODE_TEXT_MAX_LENGTH = 20;

const accountCodeTextSchema = z
    .string()
    .trim()
    .min(1)
    .max(ACCOUNT_CODE_TEXT_MAX_LENGTH)
    .nullable()
    .optional();

export const createAccountBodySchema = z.object({
    name: z.string().trim().min(1),
    type: accountTypeSchema,
    codeText: accountCodeTextSchema,
    entityIds: z.array(z.number()).min(1),
    basis: z.nativeEnum(AccountingLedgerBasis).optional(),
    parentGroupId: z.string().uuid().nullable().optional(),
});

export const updateAccountBodySchema = z.object({
    name: z.string().optional(),
    type: accountTypeSchema.optional(),
    codeText: accountCodeTextSchema,
    basis: z.nativeEnum(AccountingLedgerBasis).optional(),
    parentGroupId: z.string().uuid().nullable().optional(),
    form1099Box: form1099BoxSchema,
});

export type UpdateAccountBody = z.infer<typeof updateAccountBodySchema>;

export const bulkAccountOperationItemSchema = z.object({
    accountId: z.string().uuid(),
    entityId: z.number(),
});

export type BulkAccountOperationItem = z.infer<
    typeof bulkAccountOperationItemSchema
>;

export const bulkAccountOperationBodySchema = z.array(
    bulkAccountOperationItemSchema,
);

export type BulkAccountOperationBody = z.infer<
    typeof bulkAccountOperationBodySchema
>;

export const bulkAccountOperationResponseItemSchema = z.object({
    id: z.string().uuid(),
    success: z.boolean(),
});

export type BulkAccountOperationResponseItem = z.infer<
    typeof bulkAccountOperationResponseItemSchema
>;

export const bulkAccountOperationResponseSchema = z.array(
    bulkAccountOperationResponseItemSchema,
);

export type BulkAccountOperationResponse = z.infer<
    typeof bulkAccountOperationResponseSchema
>;

export const getCoaLastUpdatedByEntityIdsQuerySchema = z.object({
    entityIds: z.array(z.coerce.number().int().positive()).min(1).max(200),
});

export type GetCoaLastUpdatedByEntityIdsQuery = z.infer<
    typeof getCoaLastUpdatedByEntityIdsQuerySchema
>;

export const mergeAccountsBodySchema = z.object({
    sourceAccountId: z.string().uuid(),
    targetAccountId: z.string().uuid(),
    // App-only consent; the Platform API and CLI never send it.
    mergeUnlockedPeriodsOnly: z.boolean().optional(),
});

export type MergeAccountsBody = z.infer<typeof mergeAccountsBodySchema>;

export const reassignAccountRoleBodySchema = z.object({
    role: z.nativeEnum(AccountRole),
    targetAccountId: z.string().uuid(),
});

export type ReassignAccountRoleBody = z.infer<
    typeof reassignAccountRoleBodySchema
>;

export enum AccountMergeBlockerCode {
    SAME_ACCOUNT = "same_account",
    DIFFERENT_CLASS = "different_class",
    SOURCE_IS_FINANCIAL = "source_is_financial",
    TARGET_HAS_ROLE = "target_has_role",
    TARGET_TYPE_INELIGIBLE_FOR_ROLE = "target_type_ineligible_for_role",
    TARGET_IS_FINANCIAL = "target_is_financial",
    SOURCE_ARCHIVED = "source_archived",
    TARGET_ARCHIVED = "target_archived",
    LOCKED_PERIOD = "locked_period",
    LOCKED_PERIOD_MUST_BE_UNLOCKED = "locked_period_must_be_unlocked",
    TARGET_RECONCILED = "target_reconciled",
    BOTH_HAVE_OPENING_BALANCE = "both_have_opening_balance",
    OPENING_BALANCE_CLEARING_CONFLICT = "opening_balance_clearing_conflict",
    SOURCE_USED_BY_SCHEDULE = "source_used_by_schedule",
}

export const reconciliationRangeSchema = z.object({
    openingDate: z.string(),
    closingDate: z.string(),
});

export type ReconciliationRangeDto = z.infer<typeof reconciliationRangeSchema>;

export const accountMergeSideSchema = z.enum(["source", "target"]);

export type AccountMergeSide = z.infer<typeof accountMergeSideSchema>;

const simpleBlockerSchema = z.object({
    code: z.enum([
        AccountMergeBlockerCode.SAME_ACCOUNT,
        AccountMergeBlockerCode.DIFFERENT_CLASS,
        AccountMergeBlockerCode.SOURCE_IS_FINANCIAL,
        AccountMergeBlockerCode.TARGET_HAS_ROLE,
        AccountMergeBlockerCode.TARGET_TYPE_INELIGIBLE_FOR_ROLE,
        AccountMergeBlockerCode.TARGET_IS_FINANCIAL,
        AccountMergeBlockerCode.SOURCE_ARCHIVED,
        AccountMergeBlockerCode.TARGET_ARCHIVED,
        AccountMergeBlockerCode.BOTH_HAVE_OPENING_BALANCE,
        AccountMergeBlockerCode.OPENING_BALANCE_CLEARING_CONFLICT,
        AccountMergeBlockerCode.SOURCE_USED_BY_SCHEDULE,
    ]),
    message: z.string(),
});

const lockedPeriodBlockerSchema = z.object({
    code: z.literal(AccountMergeBlockerCode.LOCKED_PERIOD),
    message: z.string(),
    lockDate: z.string(),
});

const lockedPeriodMustBeUnlockedBlockerSchema = z.object({
    code: z.literal(AccountMergeBlockerCode.LOCKED_PERIOD_MUST_BE_UNLOCKED),
    message: z.string(),
    lockDate: z.string(),
});

const targetReconciledBlockerSchema = z.object({
    code: z.literal(AccountMergeBlockerCode.TARGET_RECONCILED),
    message: z.string(),
    overlappingRanges: z.array(reconciliationRangeSchema),
});

export const accountMergeBlockerSchema = z.discriminatedUnion("code", [
    simpleBlockerSchema,
    lockedPeriodBlockerSchema,
    lockedPeriodMustBeUnlockedBlockerSchema,
    targetReconciledBlockerSchema,
]);

export type AccountMergeBlocker = z.infer<typeof accountMergeBlockerSchema>;

export type LockedPeriodBlocker = z.infer<typeof lockedPeriodBlockerSchema>;

export function isLockedPeriodBlocker(
    blocker: AccountMergeBlocker,
): blocker is LockedPeriodBlocker {
    return blocker.code === AccountMergeBlockerCode.LOCKED_PERIOD;
}

export const ACCOUNT_MERGE_BLOCKER_MESSAGES: Record<
    AccountMergeBlockerCode,
    string
> = {
    [AccountMergeBlockerCode.SAME_ACCOUNT]:
        "An account cannot be merged into itself",
    [AccountMergeBlockerCode.DIFFERENT_CLASS]:
        "Accounts must be in the same class to be merged",
    [AccountMergeBlockerCode.SOURCE_IS_FINANCIAL]:
        "An account linked to a financial account cannot be merged into another",
    [AccountMergeBlockerCode.TARGET_HAS_ROLE]:
        "The target account already has a system role",
    [AccountMergeBlockerCode.TARGET_TYPE_INELIGIBLE_FOR_ROLE]:
        "The selected account's type cannot hold this system role",
    [AccountMergeBlockerCode.TARGET_IS_FINANCIAL]:
        "An account holding a system role or intercompany pair cannot be merged into an account linked to a financial account",
    [AccountMergeBlockerCode.SOURCE_ARCHIVED]:
        "An archived account cannot be merged",
    [AccountMergeBlockerCode.TARGET_ARCHIVED]:
        "An account cannot be merged into an archived account",
    [AccountMergeBlockerCode.LOCKED_PERIOD]:
        "The account being merged away has activity in a locked period",
    [AccountMergeBlockerCode.LOCKED_PERIOD_MUST_BE_UNLOCKED]:
        "Locked periods must be unlocked to merge this account.",
    [AccountMergeBlockerCode.TARGET_RECONCILED]:
        "The target account has reconciled activity in the affected period",
    [AccountMergeBlockerCode.BOTH_HAVE_OPENING_BALANCE]:
        "Both accounts have an opening balance",
    [AccountMergeBlockerCode.OPENING_BALANCE_CLEARING_CONFLICT]:
        "One account's opening balance clears into the other, so merging them would leave it clearing into itself",
    [AccountMergeBlockerCode.SOURCE_USED_BY_SCHEDULE]:
        "The account being merged away is used by a schedule or schedule template",
};

export const openingBalanceRowSummarySchema = z.object({
    kind: z.enum(["main", "adjustment"]),
    date: z.string(),
    total: z.number(),
});

export type OpeningBalanceRowSummary = z.infer<
    typeof openingBalanceRowSummarySchema
>;

export const mergeAccountsPreviewQuerySchema = z.object({
    sourceAccountId: z.string().uuid(),
    targetAccountId: z.string().uuid(),
});

export type MergeAccountsPreviewQuery = z.infer<
    typeof mergeAccountsPreviewQuerySchema
>;

export const mergeAccountsPreviewResponseSchema = z.object({
    isMergeable: z.boolean(),
    blockers: z.array(accountMergeBlockerSchema),
    openingBalance: z.object({
        source: z.array(openingBalanceRowSummarySchema),
    }),
    impact: z.object({ journalEntryLineCount: z.number().int() }),
});

export type MergeAccountsPreviewResponse = z.infer<
    typeof mergeAccountsPreviewResponseSchema
>;

export const bulkUpdateAccountsBodySchema = z.object({
    accounts: z.array(bulkAccountOperationItemSchema).min(1),
    name: z.string().optional(),
    type: accountTypeSchema.optional(),
    codeText: accountCodeTextSchema,
    parentGroupId: z.string().uuid().nullable().optional(),
    form1099Box: form1099BoxSchema,
});

export type BulkUpdateAccountsBody = z.infer<
    typeof bulkUpdateAccountsBodySchema
>;
